import { Star } from "lucide-react"
import { Container } from "@/components/layout/Container"
import { Card, CardContent } from "@/components/ui/card"
import { Marquee } from "@/components/sections/Marquee"
import { cn } from "@/lib/utils"

export interface Testimonial {
	name: string
	avatar: string
	/** Calificación de 1 a 5. */
	rating: number
	quote: string
}

interface TestimonialsProps {
	title?: React.ReactNode
	subtitle?: string
	items: Testimonial[]
}

function StarRating({ rating }: { rating: number }) {
	return (
		<div className="flex gap-0.5" role="img" aria-label={`${rating} de 5 estrellas`}>
			{Array.from({ length: 5 }).map((_, i) => (
				<Star
					key={i}
					className={cn(
						"size-4",
						i < rating ? "fill-amber-400 text-amber-400" : "fill-muted-foreground/20 text-muted-foreground/20"
					)}
				/>
			))}
		</div>
	)
}

function TestimonialCard({ name, avatar, rating, quote }: Testimonial) {
	return (
		<Card size="sm" className="w-[300px] gap-4 shadow-sm ring-foreground/15 sm:w-[340px]">
			<CardContent className="space-y-4">
				<div className="flex items-center gap-3">
					<img
						src={avatar}
						alt={name}
						className="size-11 shrink-0 rounded-full object-cover ring-1 ring-foreground/10"
						loading="lazy"
					/>
					{/* A la derecha del avatar: estrellas arriba, nombre debajo. */}
					<div className="space-y-1">
						<StarRating rating={rating} />
						<span className="block text-sm font-medium">{name}</span>
					</div>
				</div>
				<p className="text-pretty text-sm leading-relaxed text-card-foreground">“{quote}”</p>
			</CardContent>
		</Card>
	)
}

/**
 * Muro de testimonios sobre fondo oscuro con dos filas en movimiento continuo
 * (derecha → izquierda). Las filas usan distinta velocidad y ancho de tarjeta
 * para que queden desfasadas como ladrillos, no alineadas en cuadrícula. Al
 * pasar el cursor las filas no se detienen: siguen a la mitad de su velocidad.
 */
export function Testimonials({ title, subtitle, items }: TestimonialsProps) {
	const mid = Math.ceil(items.length / 2)
	const rowTop = items.slice(0, mid)
	const rowBottom = items.slice(mid)

	return (
		<section className="bg-muted py-20 md:py-28">
			{(title || subtitle) && (
				<Container className="text-center">
					{title && (
						<h2 className="text-balance text-2xl font-semibold tracking-tight sm:text-3xl md:text-4xl">{title}</h2>
					)}
					{subtitle && <p className="mx-auto mt-4 max-w-2xl text-pretty text-muted-foreground">{subtitle}</p>}
				</Container>
			)}

			{/* Las filas desbordan a todo el ancho (fuera del Container) con
			    difuminado en los bordes para un corte elegante. */}
			<div className="mt-12 flex flex-col [-webkit-mask-image:linear-gradient(to_right,transparent,black_15%,black_85%,transparent)] mask-[linear-gradient(to_right,transparent,black_15%,black_85%,transparent)]">
				{/* `py-2` evita que el `overflow-hidden` recorte el borde (ring) superior
				    e inferior de las tarjetas, y a la vez deja entre las dos filas el mismo
				    espacio (8px+8px=16px) que el gap horizontal entre tarjetas (`gap-4`). */}
				<Marquee duration="160s" hoverSpeed={0.5} className="py-2">
					{rowTop.map((t, i) => (
						<TestimonialCard key={i} {...t} />
					))}
				</Marquee>
				{/* Offset inicial + velocidad distinta ⇒ desfase de ladrillos. */}
				<Marquee duration="160s" hoverSpeed={0.5} className="py-2 ps-[60px] sm:ps-[140px]">
					{rowBottom.map((t, i) => (
						<TestimonialCard key={i} {...t} />
					))}
				</Marquee>
			</div>
		</section>
	)
}
